/**
 * MonthView
 * Simple month grid with appointment count indicators.
 */
import { useMemo } from "react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  format,
  isSameMonth,
  isToday,
} from "date-fns";
import { de } from "date-fns/locale";
import { useQuery } from "convex/react";
import { api } from "@jourfx/convex/client";

const DAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

interface MonthViewProps {
  currentDate: Date;
  onDateSelect?: (date: Date) => void;
  gutachterId?: string;
}

export function MonthView({ currentDate, onDateSelect, gutachterId }: MonthViewProps) {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const days = useMemo(() => {
    const list: Date[] = [];
    let cursor = gridStart;
    while (cursor <= gridEnd) {
      list.push(cursor);
      cursor = addDays(cursor, 1);
    }
    return list;
  }, [gridStart, gridEnd]);

  const appointments = useQuery(api.appointments.index.listByDateRange, {
    startDate: gridStart.getTime(),
    endDate: gridEnd.getTime(),
    gutachterId: gutachterId || undefined,
  });

  const counts = useMemo(() => {
    const map = new Map<string, number>();
    if (!appointments) return map;
    for (const apt of appointments) {
      const key = format(new Date(apt.scheduledAt), "yyyy-MM-dd");
      map.set(key, (map.get(key) || 0) + 1);
    }
    return map;
  }, [appointments]);

  return (
    <div className="rounded-lg border bg-card">
      <div className="grid grid-cols-7 border-b bg-muted/50 text-xs text-muted-foreground">
        {DAY_LABELS.map((label) => (
          <div key={label} className="px-2 py-2 text-center">
            {label}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const key = format(day, "yyyy-MM-dd");
          const count = counts.get(key) || 0;
          const inMonth = isSameMonth(day, currentDate);
          return (
            <button
              key={key}
              className={`relative h-20 border-b border-r p-2 text-left text-sm transition-colors hover:bg-accent/50 ${
                inMonth ? "bg-background" : "bg-muted/30 text-muted-foreground"
              }`}
              onClick={() => onDateSelect?.(day)}
            >
              <span
                className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${
                  isToday(day) ? "bg-primary text-primary-foreground" : ""
                }`}
              >
                {format(day, "d", { locale: de })}
              </span>
              {count > 0 && (
                <div className="mt-1 flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-primary" />
                  <span className="text-xs text-muted-foreground">{count}</span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
